// ============================================================================
// DRAWING WORK ITEM HUB
// Sub-navigation inside WorkItemDetailModal "Reference Docs" tab.
// Aligns drawings with overview-style hubs + WorkItemPreviewModal for formal
// transmittal PDFs (Unified Report Engine: drawing_transmittal).
// ============================================================================

'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileText,
  LayoutDashboard,
  Layers,
  PenTool,
  Send,
  Printer,
  Info,
  Loader2,
  Mail,
} from 'lucide-react';
import type { WorkItem } from '@/components/UniversalWorkItems';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import type { DocumentType } from '@/lib/reports/types';
import DrawingManagerV3 from '@/components/shared/DrawingManagerV3';
import DrawingTransmittalModal from '@/components/shared/DrawingManagerV3/DrawingTransmittal';
import type { DrawingTransmittal } from '@/components/shared/DrawingManagerV3/types';
import FieldServiceSignatureTab from '@/components/tabs/FieldServiceSignatureTab';
import WorkItemPreviewModal from '@/components/work-items/WorkItemPreviewModal';
import { getTimerAggregationModeFromCustomFields, useTimerAggregationMode } from './billingDashboardUtils';
import type { Drawing } from '@/components/shared/DrawingManagerV3/types';

type HubTab = 'overview' | 'drawings' | 'versions' | 'signatures' | 'transmittals';

export interface DrawingWorkItemHubProps {
  /** UUID work item id — same as WorkItemDetailModal / TaskOverviewTab */
  workItemId: string;
  workItem: WorkItem;
  isActive: boolean;
  onDrawingsChange?: (count: number) => void;
  readOnly?: boolean;
}

const getToken = () => authService.getTokens()?.access_token || '';

export default function DrawingWorkItemHub({
  workItemId,
  workItem,
  isActive,
  onDrawingsChange,
  readOnly = false,
}: DrawingWorkItemHubProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark';
  const effectiveId = workItemId || workItem.id;

  const [hubTab, setHubTab] = useState<HubTab>('overview');
  const [drawingsForModal, setDrawingsForModal] = useState<Drawing[]>([]);
  const [versionsRows, setVersionsRows] = useState<Drawing[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [transmittalIds, setTransmittalIds] = useState<string[]>([]);
  const [showTransmittalModal, setShowTransmittalModal] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [transmittals, setTransmittals] = useState<
    Array<{
      id: string;
      transmittal_number: string;
      subject?: string | null;
      status?: string | null;
      sent_at?: string | null;
      created_at?: string | null;
    }>
  >([]);
  const [transmittalsLoading, setTransmittalsLoading] = useState(false);
  const timerAggregationMode = useTimerAggregationMode();
  // Per-item stored mode wins over the browser-global preference.
  const effectiveTimerAggregationMode = getTimerAggregationModeFromCustomFields(workItem?.custom_fields)
    ?? timerAggregationMode;

  const forcedDocType: DocumentType = 'drawing_transmittal';

  const loadDrawingRows = useCallback(async () => {
    const token = getToken();
    if (!token || !effectiveId) return;
    setVersionsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('work_item_id', effectiveId);
      params.set('include_stats', 'true');
      const res = await fetch(`/api/v1/drawings?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const list: Drawing[] = data.drawings || [];
      setVersionsRows(list);
      setDrawingsForModal(list);
      onDrawingsChange?.(list.length);
    } catch {
      /* ignore */
    } finally {
      setVersionsLoading(false);
    }
  }, [effectiveId, onDrawingsChange]);

  const loadTransmittals = useCallback(async () => {
    const token = getToken();
    if (!token || !effectiveId) return;
    setTransmittalsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('work_item_id', effectiveId);
      params.set('limit', '30');
      const res = await fetch(`/api/v1/drawings/transmittals?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setTransmittals(data.transmittals || []);
    } catch {
      /* ignore */
    } finally {
      setTransmittalsLoading(false);
    }
  }, [effectiveId]);

  useEffect(() => {
    if (!isActive) return;
    loadDrawingRows();
    loadTransmittals();
  }, [isActive, loadDrawingRows, loadTransmittals]);

  const tabButton = (id: HubTab, label: string, Icon: typeof FileText) => (
    <button
      type="button"
      key={id}
      onClick={() => setHubTab(id)}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
        hubTab === id
          ? isDark
            ? 'bg-blue-600 text-white'
            : 'bg-blue-600 text-white'
          : isDark
            ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );

  const publishedCount = useMemo(
    () => versionsRows.filter((d) => d.status === 'published' || d.is_published).length,
    [versionsRows],
  );

  const handleSendTransmittal = async (
    transmittal: Omit<DrawingTransmittal, 'id' | 'created_at' | 'updated_at'>,
  ) => {
    const token = getToken();
    const toEmail = transmittal.to_email || '';
    const recipients = [toEmail]
      .filter((v) => typeof v === 'string' && v.trim().length > 0)
      .map((v) => v.trim());

    const drawingIds =
      transmittalIds.length > 0 ? transmittalIds : drawingsForModal.map((d) => d.id);

    const res = await fetch('/api/v1/drawings/transmittals', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        work_item_id: effectiveId,
        subject: transmittal.subject,
        description: transmittal.notes || null,
        purpose: transmittal.reason || 'for_information',
        drawings: drawingIds,
        recipients,
        remarks: transmittal.notes || null,
        send_email: recipients.length > 0,
      }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({ error: 'Failed to create transmittal' }));
      notify.error(data.error || 'Failed to create transmittal');
      return;
    }
    notify.success('Transmittal recorded');
    setShowTransmittalModal(false);
    setTransmittalIds([]);
    loadTransmittals();
  };

  return (
    <div
      className={`flex flex-col min-h-[420px] ${isDark ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}
    >
      <div
        className={`flex flex-wrap items-center gap-2 px-3 py-2 border-b ${isDark ? 'border-gray-800 bg-gray-900/90' : 'border-gray-200 bg-white'}`}
      >
        {tabButton('overview', 'Overview', LayoutDashboard)}
        {tabButton('drawings', 'Drawings', FileText)}
        {tabButton('versions', 'Versions', Layers)}
        {tabButton('signatures', 'Signatures', PenTool)}
        {tabButton('transmittals', 'Transmittals', Send)}
      </div>

      {hubTab === 'overview' && (
        <div className="p-4 space-y-4">
          <div
            className={`rounded-xl border p-4 flex gap-3 ${isDark ? 'border-blue-900/40 bg-blue-950/30' : 'border-blue-100 bg-blue-50/80'}`}
          >
            <Info className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-blue-300' : 'text-blue-600'}`} />
            <div className={`text-sm ${isDark ? 'text-blue-100' : 'text-blue-900'}`}>
              <p className="font-medium">Workflow status for this job or task</p>
              <p className={`mt-1 ${isDark ? 'text-blue-200/90' : 'text-blue-800/90'}`}>
                Use the <strong>Details</strong> panel in the right sidebar to change the work item&apos;s operational
                status. Drawing lifecycle actions (submit, approve, publish) stay on drawing cards below.
              </p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'}`}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-70">Drawing files</p>
              <p className="mt-2 text-2xl font-semibold">{versionsRows.length}</p>
            </div>
            <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'}`}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-70">Published / issued</p>
              <p className="mt-2 text-2xl font-semibold">{publishedCount}</p>
            </div>
            <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'}`}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-70">Transmittals</p>
              <p className="mt-2 text-2xl font-semibold">{transmittals.length}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900"
            >
              <Printer className="w-4 h-4" />
              Formal register (preview / PDF / email)
            </button>
            <button
              type="button"
              onClick={() => {
                setTransmittalIds(drawingsForModal.map((d) => d.id));
                setShowTransmittalModal(true);
              }}
              className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium border-gray-300 dark:border-gray-600"
            >
              <Mail className="w-4 h-4" />
              Create transmittal
            </button>
            <button
              type="button"
              onClick={() => setHubTab('drawings')}
              className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium border-gray-300 dark:border-gray-600"
            >
              <FileText className="w-4 h-4" />
              Open drawing library
            </button>
          </div>
        </div>
      )}

      {hubTab === 'drawings' && (
        <div className="flex-1 min-h-[480px] flex flex-col">
          <DrawingManagerV3
            workItemId={effectiveId}
            mode={readOnly ? 'view-only' : 'manage'}
            showUpload={!readOnly}
            showFolders
            showBIMViewer={false}
            defaultStatusFilter="all"
            showApprovalWorkflow={!readOnly}
            useCloudStorage
            showRecordKeeping={false}
            embeddedInWorkItemDetail
            onDrawingsLoaded={onDrawingsChange}
          />
        </div>
      )}

      {hubTab === 'versions' && (
        <div className="p-4">
          {versionsLoading ? (
            <div className="flex items-center gap-2 text-sm opacity-70">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading versions…
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className={isDark ? 'bg-gray-800/80' : 'bg-gray-100'}>
                  <tr>
                    <th className="px-3 py-2 text-left">Drawing</th>
                    <th className="px-3 py-2 text-left">Number</th>
                    <th className="px-3 py-2 text-left">Rev</th>
                    <th className="px-3 py-2 text-left">Version</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {versionsRows.map((row) => (
                    <tr key={row.id} className="border-t border-gray-200 dark:border-gray-700">
                      <td className="px-3 py-2">{row.drawing_name}</td>
                      <td className="px-3 py-2">{row.drawing_number || '—'}</td>
                      <td className="px-3 py-2">{row.revision}</td>
                      <td className="px-3 py-2">{row.version}</td>
                      <td className="px-3 py-2">{row.status?.replace(/_/g, ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {hubTab === 'signatures' && (
        <div className="p-2">
          <FieldServiceSignatureTab workItemId={effectiveId} isActive={hubTab === 'signatures' && isActive} />
        </div>
      )}

      {hubTab === 'transmittals' && (
        <div className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setTransmittalIds(drawingsForModal.map((d) => d.id));
                setShowTransmittalModal(true);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <Send className="w-4 h-4" />
              New transmittal
            </button>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium border-gray-300 dark:border-gray-600"
            >
              <Printer className="w-4 h-4" />
              Printable register
            </button>
          </div>
          {transmittalsLoading ? (
            <Loader2 className="w-5 h-5 animate-spin opacity-50" />
          ) : (
            <ul className="space-y-2">
              {transmittals.map((t) => (
                <li
                  key={t.id}
                  className={`rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-white'}`}
                >
                  <span className="font-medium">{t.transmittal_number}</span>
                  <span className="mx-2 opacity-60">·</span>
                  {t.subject || 'Drawing transmittal'}
                  <span className="mx-2 opacity-60">·</span>
                  <span className="capitalize">{t.status || 'draft'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <DrawingTransmittalModal
        drawings={transmittalIds.length ? versionsRows.filter((d) => transmittalIds.includes(d.id)) : drawingsForModal}
        isOpen={showTransmittalModal}
        onClose={() => {
          setShowTransmittalModal(false);
          setTransmittalIds([]);
        }}
        onSend={handleSendTransmittal}
        projectName={workItem.title || undefined}
        projectNumber={workItem.item_number || undefined}
      />

      <WorkItemPreviewModal
        workItem={workItem}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        forcedDocumentType={forcedDocType}
        comprehensive={false}
        timerAggregationMode={effectiveTimerAggregationMode}
      />
    </div>
  );
}
